import { extendTheme } from "@chakra-ui/react";

/*
 * Corporate Azure design tokens (Portal Design Rework, Phase 0b).
 * Source of truth: ToDo/PORTAL_DESIGN_REWORK_19.05/PLAN.md + the reference
 * template asap_hub_corporate_mes-pools_donnees-aligne.html (final :root block).
 *
 *   navy   #0B1F3A   sidebar, module mark, primary text accents
 *   navy-2 #12345A
 *   navy-3 #1E4D78
 *   primary#153B69   buttons, active tab underline, links  (Chakra `brand`)
 *   mint   #1BAE9A   status dots, success, sidebar active accent (Chakra `green`)
 *   bg     #F2F6FA   app background (gradient #F7FAFD -> #EEF4F8)
 *   panel  #FFFFFF
 *   line   #D6E0EA   borders
 *   muted  #617386   secondary text / labels
 *   text   #102033
 *   soft   #F7FAFC   active #EAF2F8   danger #C93A45
 *
 * Migration note: the app uses colorScheme="brand"/"blue"/"teal"/"cyan" widely.
 * Those all resolve to the new navy `primary` scale so the global shift is a
 * recolor, not a structural change. `green` resolves to `mint` (status/success).
 */

// primary / brand — navy-blue #153B69 (hover -> navy #0B1F3A at 600)
const primaryScale = {
  50: "#e9eef5",
  100: "#c7d6e7",
  200: "#a1bbd6",
  300: "#789dc3",
  400: "#456c9b",
  500: "#153B69",
  600: "#0B1F3A",
  700: "#0a1c34",
  800: "#08182c",
  900: "#050f1d",
};

// navy — darkest institutional surfaces (sidebar, module mark)
const navyScale = {
  50: "#e8ebef",
  100: "#c5cdd6",
  200: "#9fabb9",
  300: "#78899c",
  400: "#3f5871",
  500: "#0B1F3A",
  600: "#0a1c34",
  700: "#08182c",
  800: "#071425",
  900: "#050f1c",
};

// mint / success — #1BAE9A
const mintScale = {
  50: "#e8f8f5",
  100: "#c4ede6",
  200: "#9ce1d6",
  300: "#71d4c5",
  400: "#45c5b2",
  500: "#1BAE9A",
  600: "#179683",
  700: "#127a6b",
  800: "#0d5e53",
  900: "#083a33",
};

const dangerScale = {
  50: "#faeaeb",
  100: "#f2c6c9",
  200: "#e89da2",
  300: "#de7077",
  400: "#d6565e",
  500: "#C93A45",
  600: "#b32f3a",
  700: "#93262f",
  800: "#731d25",
  900: "#54151b",
};

const INTER = `'Inter', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif`;

const theme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
  fonts: {
    heading: INTER,
    body: INTER,
  },
  colors: {
    // Brand + the legacy schemes the app already uses, all unified onto navy primary
    brand: primaryScale,
    primary: primaryScale,
    teal: primaryScale,
    blue: primaryScale,
    cyan: primaryScale,
    // Status / success scheme -> mint
    green: mintScale,
    mint: mintScale,
    navy: navyScale,
    danger: dangerScale,
    // Flat semantic tokens for direct use in `bg`/`color`/`borderColor` props
    portal: {
      navy: "#0B1F3A",
      navy2: "#12345A",
      navy3: "#1E4D78",
      primary: "#153B69",
      mint: "#1BAE9A",
      bg: "#F2F6FA",
      panel: "#FFFFFF",
      soft: "#F7FAFC",
      active: "#EAF2F8",
      line: "#D6E0EA",
      muted: "#617386",
      text: "#102033",
      danger: "#C93A45",
    },
  },
  radii: {
    // Template uses 10px on controls/cards/buttons; bump the default `md` so
    // existing components pick up the rounder corners without per-component edits.
    md: "10px",
    control: "10px",
    card: "10px",
    mark: "14px",
  },
  shadows: {
    panel: "0 10px 24px rgba(16,32,51,.045)",
    kpi: "0 10px 24px rgba(16,32,51,.045)",
    brandbar: "0 8px 24px rgba(16,32,51,.05)",
    mark: "0 10px 22px rgba(11,31,58,.18)",
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: "10px",
        fontWeight: 600,
      },
      defaultProps: {
        colorScheme: "brand",
      },
      variants: {
        // Solid navy CTA (template .btn.primary): #153B69, hover navy #0B1F3A
        primary: {
          bg: "portal.primary",
          color: "white",
          border: "1px solid",
          borderColor: "portal.primary",
          fontWeight: 800,
          boxShadow: "0 8px 16px rgba(21,59,105,.18)",
          _hover: { bg: "portal.navy", borderColor: "portal.navy", _disabled: { bg: "portal.primary" } },
          _active: { bg: "portal.navy" },
        },
        // Outlined neutral action (template .btn.secondary)
        secondary: {
          bg: "white",
          color: "portal.primary",
          border: "1px solid",
          borderColor: "#B7C8D8",
          fontWeight: 800,
          _hover: { bg: "portal.bg", borderColor: "#8FAAC3" },
          _active: { bg: "portal.active" },
        },
      },
    },
    Heading: {
      baseStyle: {
        color: "portal.text",
        letterSpacing: "-0.02em",
      },
    },
    Text: {
      baseStyle: {
        color: "portal.text",
      },
    },
    Link: {
      baseStyle: {
        color: "portal.primary",
      },
    },
    Tag: {
      defaultProps: { colorScheme: "brand" },
    },
    Badge: {
      defaultProps: { colorScheme: "brand" },
    },
    Checkbox: {
      defaultProps: { colorScheme: "brand" },
    },
    Switch: {
      defaultProps: { colorScheme: "brand" },
    },
    Radio: {
      defaultProps: { colorScheme: "brand" },
    },
    Progress: {
      defaultProps: { colorScheme: "brand" },
    },
    Input: {
      baseStyle: {
        field: { borderRadius: "10px" },
      },
      variants: {
        outline: {
          field: {
            borderColor: "portal.line",
            _focusVisible: {
              borderColor: "portal.primary",
              boxShadow: "0 0 0 1px var(--chakra-colors-portal-primary)",
            },
          },
        },
      },
    },
    Select: {
      baseStyle: {
        field: { borderRadius: "10px" },
      },
      variants: {
        outline: {
          field: {
            borderColor: "portal.line",
            _focusVisible: {
              borderColor: "portal.primary",
              boxShadow: "0 0 0 1px var(--chakra-colors-portal-primary)",
            },
          },
        },
      },
    },
    Textarea: {
      baseStyle: { borderRadius: "10px" },
      variants: {
        outline: {
          borderColor: "portal.line",
          _focusVisible: {
            borderColor: "portal.primary",
            boxShadow: "0 0 0 1px var(--chakra-colors-portal-primary)",
          },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: "card",
          borderWidth: "1px",
          borderColor: "portal.line",
          boxShadow: "panel",
          bg: "portal.panel",
        },
      },
    },
    Table: {
      baseStyle: {
        th: {
          textTransform: "uppercase",
          letterSpacing: "0.055em",
          fontWeight: 900,
          color: "#557084",
          borderColor: "portal.line",
        },
        td: {
          borderColor: "#edf2f5",
          color: "#243543",
        },
      },
    },
    Tabs: {
      variants: {
        line: {
          tab: {
            fontWeight: 700,
            color: "#627786",
            _selected: {
              color: "portal.navy",
              borderColor: "portal.primary",
            },
          },
        },
      },
    },
  },
  styles: {
    global: {
      // The bare landing-page h1-h6 leak is now scoped to body.marketing-page
      // (Phase 0a). Keep a low-specificity fallback so any non-Chakra heading in
      // the portal reads the design text color instead of browser default.
      "h1, h2, h3, h4, h5, h6": { color: "portal.text" },
    },
  },
});

export default theme;
